import { Fragment, useId, useState } from "react";
import type { CatalogAssetResponse, Size } from "../../generated/api/types.gen";
import type { SourceCatalogState } from "../../hooks/useSourceCatalog";
import { sizeParts } from "../../lib/source-catalog";
import { ModalCloseButton, ModalShell } from "../ModalShell";
import { AddToAssetsButton } from "./AddToAssetsButton";
import { SourceAssetSwatch } from "./SourceAssetSwatch";

// From sm up the card never clips, so the project menu can hang below it as
// the design shows. It fits the viewport instead: the photo shrinks on short
// screens and the details between the header and the actions scroll. Stacked
// on a phone, the whole card scrolls.
const CARD =
  "relative flex w-full max-w-[792px] flex-col gap-6 rounded-2xl bg-[var(--bg-modal)] p-6 shadow-xl max-sm:max-h-[90vh] max-sm:overflow-y-auto sm:max-h-[calc(100dvh-2rem)] sm:flex-row";
// 360px, or less when the viewport is too short for the photo and the order
// details below it inside the card's padding.
const PHOTO_COLUMN = "sm:w-[min(360px,calc(100dvh-140px))]";

const LABEL = "text-xs leading-[1.6] font-medium text-white";
const MUTED = "text-xs leading-[1.6] text-[var(--text-muted)]";

interface SourceAssetDetailsModalProps {
  asset: CatalogAssetResponse;
  catalog: Pick<SourceCatalogState, "saveToLibrary" | "addToProject">;
  onClose: () => void;
}

/**
 * A catalog product's details: its swatch or photo, collection, name, maker
 * and designer, its smallest size, description, how to order it (its SKU when it has one, and a
 * link to its product page), and adding it to My Assets or one of the user's
 * projects.
 */
export function SourceAssetDetailsModal({
  asset,
  catalog,
  onClose,
}: SourceAssetDetailsModalProps) {
  const titleId = useId();
  const [addedTo, setAddedTo] = useState<string | null>(null);

  return (
    <ModalShell
      onClose={onClose}
      ariaContext="product details"
      labelledBy={titleId}
      cardClassName={CARD}
      closeAffordance="none"
    >
      <div className={`flex w-full shrink-0 flex-col gap-4 ${PHOTO_COLUMN}`}>
        <SourceAssetSwatch
          asset={asset}
          className="aspect-square w-full"
          showCategory={false}
        />
        <OrderDetails asset={asset} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-6 sm:min-h-0">
        <ProductHeading asset={asset} titleId={titleId} onClose={onClose} />
        <div className="flex flex-1 flex-col gap-6 sm:min-h-0 sm:overflow-y-auto">
          {asset.sizes?.[0] && <Dimensions size={asset.sizes[0]} />}
          {asset.description && (
            <div className="flex flex-col gap-1">
              <p className={LABEL}>Description</p>
              <p className={`whitespace-pre-line ${MUTED}`}>
                {asset.description}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3">
          {addedTo && (
            <p role="status" className={MUTED}>
              Added to {addedTo}
            </p>
          )}
          <AddToAssetsButton
            inLibrary={asset.in_library}
            onSave={() => catalog.saveToLibrary(asset.id)}
            onAddToProject={async (project) => {
              setAddedTo(null);
              if (await catalog.addToProject(asset, project)) {
                setAddedTo(project.name);
              }
            }}
          />
        </div>
      </div>
    </ModalShell>
  );
}

/** Collection, name, maker and designer, beside the close control. */
function ProductHeading({
  asset,
  titleId,
  onClose,
}: {
  asset: CatalogAssetResponse;
  titleId: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {asset.collection && <p className={MUTED}>{asset.collection}</p>}
        <h2
          id={titleId}
          className="text-xl leading-6 font-bold text-[var(--text-primary)]"
        >
          {asset.name}
        </h2>
        <p className={MUTED}>
          {asset.manufacturer}
          {asset.designer && (
            <>
              <span aria-hidden="true"> • </span>
              Designed by: {asset.designer}
            </>
          )}
        </p>
      </div>
      <ModalCloseButton
        onClose={onClose}
        ariaContext="product details"
        size={20}
      />
    </div>
  );
}

/** One size in inches, with centimeters beneath: 44” W x 44” D x 30” H in. */
function Dimensions({ size }: { size: Size }) {
  const parts = sizeParts(size);
  const times = <span className="text-[10px] font-normal"> x </span>;

  return (
    <div className="flex flex-col leading-[1.6]">
      <p className="text-base font-bold text-white">
        {parts.map((part, i) => (
          <Fragment key={part.axis}>
            {i > 0 && times}
            {part.inches}” {part.axis}
          </Fragment>
        ))}
        <span className="text-[10px] font-normal"> in</span>
      </p>
      <p className="text-xs font-bold text-[var(--text-muted)]">
        {parts.map((part, i) => (
          <Fragment key={part.axis}>
            {i > 0 && times}
            {part.centimeters}cm {part.axis}
          </Fragment>
        ))}
      </p>
    </div>
  );
}

/** The SKU it is ordered by, when it has one, beside a link to its product page. */
function OrderDetails({ asset }: { asset: CatalogAssetResponse }) {
  if (!(asset.sku || asset.product_url)) return null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-1">
        <p className={`flex-1 ${LABEL}`}>
          {asset.sku && "Product Number (SKU)"}
        </p>
        {asset.product_url && (
          <a
            href={asset.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline hover:text-white ${MUTED}`}
          >
            See on {asset.manufacturer}’s site
          </a>
        )}
      </div>
      {asset.sku && <p className={MUTED}>{asset.sku}</p>}
    </div>
  );
}
